/* Created: Admin panel for the Brevo delivery webhook on the Transactiemails page.
   - Checks whether Brevo already reports delivery events (afgeleverd, uitgesteld, bounce) to this shop.
   - "Webhook instellen" registers or completes it via the Brevo API; the secret never reaches the browser.
   - Shows the exact URL and events for manual setup when automatic registration is not possible. */

import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { invokeBrevoMail } from '@/lib/transactional-mail';
import { Webhook, Loader as Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';

interface WebhookState {
  url: string;
  events: string[];
  registered: boolean;
  webhook_id: number | null;
  missing_events: string[];
  action?: 'exists' | 'created' | 'updated';
}

export default function BrevoWebhookPanel() {
  const [state, setState] = useState<WebhookState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'check' | 'register' | null>(null);

  const run = useCallback(async (action: 'webhook_status' | 'register_webhook') => {
    setBusy(action === 'webhook_status' ? 'check' : 'register');
    try {
      const data = await invokeBrevoMail<WebhookState>({ action });
      if (typeof data.url !== 'string' || !Array.isArray(data.events)) throw new Error('Onverwacht antwoord');
      setState(data);
      setError(null);
      if (action === 'register_webhook') {
        toast.success(data.action === 'exists' ? 'Webhook was al ingesteld' : 'Webhook ingesteld bij Brevo');
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Onbekende fout';
      setError(message);
      if (action === 'register_webhook') toast.error('Webhook instellen mislukt', { description: message });
    } finally {
      setBusy(null);
    }
  }, []);

  useEffect(() => { run('webhook_status'); }, [run]);

  const complete = state?.registered && state.missing_events.length === 0;

  return (
    <Card className="border border-[#e7e1d6] bg-white">
      <CardHeader className="bg-[#fcfaf6] border-b border-[#e7e1d6]">
        <CardTitle className="flex items-center gap-2 text-[#1e2b22]"><Webhook className="w-5 h-5" />Bezorgstatus via Brevo</CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            {busy === 'check' && !state ? (
              <Loader2 className="w-5 h-5 animate-spin text-[#5a6552] mt-0.5" />
            ) : complete ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            )}
            <div>
              <p className="font-semibold text-[#1e2b22]">
                {complete ? 'Brevo meldt aflevering en bounces aan de webshop'
                  : state?.registered ? 'Webhook bestaat, maar mist events'
                  : state ? 'Webhook nog niet ingesteld' : 'Status onbekend'}
              </p>
              <p className="text-sm text-[#5a6552] mt-1 max-w-2xl">
                "Afgeleverd" betekent dat de ontvangende mailserver de mail heeft aangenomen, niet dat hij is gelezen of in de
                hoofdinbox staat. Openingen en kliks worden niet gebruikt.
              </p>
              {error && <p className="text-sm text-red-700 mt-1">{error}</p>}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="border-[#e7e1d6]" disabled={!!busy} onClick={() => run('webhook_status')}>
              {busy === 'check' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Controleren
            </Button>
            {!complete && (
              <Button className="bg-[#1e2b22] hover:bg-[#2a3a2f] text-white" disabled={!!busy} onClick={() => run('register_webhook')}>
                {busy === 'register' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Webhook instellen
              </Button>
            )}
          </div>
        </div>
        {state && !complete && (
          <div className="text-xs text-[#5a6552] bg-[#fcfaf6] border border-[#e7e1d6] rounded p-4 space-y-1">
            <p>Handmatig instellen (Brevo, Transactioneel, Instellingen, Webhooks):</p>
            <p>URL: <span className="font-mono break-all">{state.url}</span></p>
            <p>Events: {state.events.join(', ')}</p>
            <p>Authenticatie: Bearer-token (wordt alleen door de knop hierboven ingevuld en hier nooit getoond).</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
